"use client";
import { IconWithAnimation } from "@/components/AnimatedIcons/AnimatedIcons";
import { formatMoney } from "@/lib/utils";
import { JsonLd, mappedProductsToJsonLd } from "@/ui/json-ld";
import { YnsLink } from "@/ui/yns-link";
import type * as Commerce from "commerce-kit";
import { motion } from "framer-motion";
import Image from "next/image";

export const ProductList = ({ products }: { products: Commerce.MappedProduct[] }) => {
	return (
		<main className="bg-black text-white">
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-7xl">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
						{products.map((product, index) => {
							// Process marketing features
							let buttonText = "Ver Detalles";
							let subtitle = "";
							const features: string[] = [];

							// Define the URL once
							const productHref = `/product/${product.metadata.slug}`;

							product.marketing_features?.forEach((item) => {
								if (item.startsWith("button: ")) {
									buttonText = item.replace("button: ", "");
								} else if (item.startsWith("Subtitle: ")) {
									subtitle = item.replace("Subtitle: ", "");
								} else {
									features.push(item);
								}
							});

							return (
								// 🟢 FIX: Wrap the entire card container in YnsLink,
								// and move the motion props here.
								<YnsLink key={product.id} href={productHref} className="block group">
									<motion.div
										// The motion.div now acts as the clickable card content
										className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-800 hover:border-red-600 transition-colors duration-300 flex flex-col h-full"
										whileHover={{ y: -5 }}
									>
										{/* Product Image - Fixed Height */}
										<div className="h-48 w-full overflow-hidden">
											{product.images[0] && (
												<Image
													src={product.images[0]}
													alt={product.name}
													width={400}
													height={400}
													className="w-full h-full object-cover"
													loading={index < 3 ? "eager" : "lazy"}
													priority={index < 3}
												/>
											)}
										</div>

										{/* Content Area - Flex-grow for consistent height */}
										<div className="p-6 flex-grow flex flex-col">
											<div className="mb-4">
												<h2 className="font-amarante text-3xl text-white">{product.name}</h2>
												{subtitle && <p className="text-red-400">{subtitle}</p>}
											</div>

											{/* Price or "A Convenir" placeholder */}
											<div className="mt-2 mb-4">
												{product.default_price?.unit_amount ? (
													<div className="inline-block text-lg text-white font-bold px-4 py-1 bg-red-600 rounded-full">
														{formatMoney({
															amount: product.default_price.unit_amount,
															currency: product.default_price.currency,
															locale: "es-ES",
														})}
													</div>
												) : (
													<div className="inline-block text-lg text-gray-300 font-bold px-4 py-1 bg-gray-700 rounded-full border border-gray-600">
														A Convenir
													</div>
												)}
											</div>

											{/* Description with distinct styling */}
											{product.description && (
												<div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex-grow-0">
													<p className="text-gray-300 italic">{product.description}</p>
												</div>
											)}

											{/* Features List */}
											{features.length > 0 && (
												<ul className="space-y-3 mt-2 pl-5 list-disc text-gray-300 flex-grow">
													{features.map((feature, i) => (
														<IconWithAnimation key={i} itemKey={i} phrase={feature} />
													))}
												</ul>
											)}
										</div>

										{/* Button - Now just styles the link content */}
										<div className="px-6 py-4 bg-gray-700/50 border-t border-gray-600">
											{/* The YnsLink around the button is removed to prevent nested links.
                                               The button is now a simple element inside the clickable YnsLink wrapper. 
                                            */}
											<motion.div
												className="p-4 text-white rounded font-amarante text-center transition-colors w-full flex justify-center items-center"
												whileHover={{
													scale: 1.1,
													boxShadow: "0 0 15px #f93d3d",
													backgroundColor: "white",
													color: "black",
													fontWeight: "900",
													fontStyle: "italic",
												}}
												whileTap={{ scale: 0.98 }}
											>
												<span className="w-full text-2xl text-center">{buttonText}</span>
											</motion.div>
										</div>
									</motion.div>
								</YnsLink>
							);
						})}
					</div>
				</div>
			</section>
			<JsonLd jsonLd={mappedProductsToJsonLd(products)} />
		</main>
	);
};
